/**
 * Organization roles and permissions, shared by the server (enforcement) and
 * the dashboard (affordances only — the server is always authoritative).
 */
import { createAccessControl } from "better-auth/plugins/access";
import { adminAc, defaultStatements, memberAc, ownerAc } from "better-auth/plugins/organization/access";

export const statements = {
  ...defaultStatements,
  // Consumed by the Better Auth api-key plugin for organization-owned keys.
  apiKey: ["create", "read", "update", "delete"],
  application: ["create", "update", "delete"],
  channel: ["create", "update", "delete"],
  notification: ["create"],
  billing: ["manage"],
} as const;

export const ac = createAccessControl(statements);

export const roles = {
  owner: ac.newRole({
    ...ownerAc.statements,
    apiKey: ["create", "read", "update", "delete"],
    application: ["create", "update", "delete"],
    channel: ["create", "update", "delete"],
    notification: ["create"],
    billing: ["manage"],
  }),
  admin: ac.newRole({
    ...adminAc.statements,
    apiKey: ["create", "read", "update", "delete"],
    application: ["create", "update", "delete"],
    channel: ["create", "update", "delete"],
    notification: ["create"],
    billing: ["manage"],
  }),
  member: ac.newRole({
    ...memberAc.statements,
    apiKey: ["read"],
    application: [],
    channel: [],
    notification: ["create"],
    billing: [],
  }),
};

export type Role = keyof typeof roles;
export type Statements = typeof statements;
export type Permission = {
  [K in keyof Statements]?: Statements[K][number][];
};

export function roleHasPermission(role: string, permission: Permission): boolean {
  // Better Auth stores multiple roles comma-separated.
  return role
    .split(",")
    .map((r) => r.trim())
    .some((r) => {
      const definition = roles[r as Role];
      return definition ? definition.authorize(permission).success : false;
    });
}
